/**
 * A queryable cache for encrypted blobs.
 *
 * The browser's HTTP cache is opaque to script: you cannot ask it what it
 * holds, so a preload button backed by it can only report progress while it is
 * running and forgets everything on reload. The Cache API is inspectable, so
 * this stores preloaded blobs somewhere we can actually count.
 *
 * What lands here is exactly what the server holds — HLS-AES-128 segments and
 * PS1-sealed manifests. Nothing is decrypted on the way in, so this cache is
 * no more revealing than the HTTP cache it replaces, and clearing it is a
 * supported operation rather than a browser setting.
 */

const CACHE_NAME = 'peepshow/blobs/v1';

/** Cache Storage needs a secure context; absent it, everything degrades. */
export function cacheAvailable(): boolean {
  return typeof caches !== 'undefined';
}

async function open(): Promise<Cache | null> {
  if (!cacheAvailable()) return null;
  try {
    return await caches.open(CACHE_NAME);
  } catch {
    return null;
  }
}

export function blobUrl(uuid: string): string {
  return `/api/blobs/${uuid}/`;
}

/**
 * Every blob uuid currently held, in one pass.
 *
 * One `keys()` call beats a `match()` per blob: a library of a few hundred
 * segments would otherwise mean a few hundred round trips through the cache
 * for every render that wants a percentage.
 */
export async function cachedUuids(): Promise<Set<string>> {
  const cache = await open();
  if (!cache) return new Set();
  const out = new Set<string>();
  for (const request of await cache.keys()) {
    const match = /\/api\/blobs\/([^/]+)\//.exec(new URL(request.url).pathname);
    if (match) out.add(match[1]);
  }
  return out;
}

/** The cached response for a blob, if we have one. */
export async function matchBlob(uuid: string): Promise<Response | undefined> {
  const cache = await open();
  if (!cache) return undefined;
  try {
    return await cache.match(blobUrl(uuid));
  } catch {
    return undefined;
  }
}

/** Store a blob response. Returns false when the write failed (quota, etc.). */
export async function putBlob(uuid: string, response: Response): Promise<boolean> {
  const cache = await open();
  if (!cache) return false;
  try {
    await cache.put(blobUrl(uuid), response);
    return true;
  } catch {
    // Quota exhaustion is the likely cause and is not fatal: playback still
    // works, it just goes to the network.
    return false;
  }
}

export async function deleteBlobs(uuids: Iterable<string>): Promise<number> {
  const cache = await open();
  if (!cache) return 0;
  let removed = 0;
  for (const uuid of uuids) {
    if (await cache.delete(blobUrl(uuid))) removed += 1;
  }
  return removed;
}

export async function clearCache(): Promise<void> {
  if (!cacheAvailable()) return;
  try {
    await caches.delete(CACHE_NAME);
  } catch {
    /* nothing we can do, and nothing depends on it */
  }
}

export interface StorageEstimate {
  /** Bytes this origin is currently using. */
  usage: number;
  /** Bytes the browser is willing to let it use. */
  quota: number;
}

/**
 * What the browser will actually allow.
 *
 * There is no fixed figure to quote: Chromium offers a share of whatever the
 * disk has free and recalculates as that changes, Firefox and Safari use their
 * own rules. Only the browser knows, so ask it rather than guess.
 *
 * The numbers cover the whole origin, not just this cache, but nothing else
 * here stores anything of consequence.
 */
export async function storageEstimate(): Promise<StorageEstimate | null> {
  if (typeof navigator === 'undefined' || !navigator.storage?.estimate) return null;
  try {
    const { usage = 0, quota = 0 } = await navigator.storage.estimate();
    return quota > 0 ? { usage, quota } : null;
  } catch {
    return null;
  }
}

/** Bytes held, for reporting. Reads each entry, so call it sparingly. */
export async function cachedBytes(uuids: Iterable<string>): Promise<number> {
  const cache = await open();
  if (!cache) return 0;
  let total = 0;
  for (const uuid of uuids) {
    const hit = await cache.match(blobUrl(uuid));
    if (!hit) continue;
    const length = hit.headers.get('content-length');
    total += length ? Number(length) : (await hit.clone().arrayBuffer()).byteLength;
  }
  return total;
}
